import { useCallback } from 'react';

import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { QueryPagination, createQueryOptions } from '../../../query';
import { useStoreApi } from '../../api';
import { storeKeys } from '../../query-key-factory';
import { EventDate, EventDateTicketInventoriesResponse } from '../../types/eventDate';
import {
  EventDateIncludeOptions,
  EventDateInventoriesSearch,
  EventDateSearch,
} from './search.types';
import { EventDatesResponse, ShowEventInventorySummaryResponse, ShowEventResponse } from './types';

const useListEventDates = <TData = EventDatesResponse<EventDate[]>>({
  eventId,
  causeId,
  include,
  pagination,
  queryOptions,
  select,
}: {
  eventId?: string;
  causeId?: string;
  include?: EventDateIncludeOptions;
  pagination?: QueryPagination;
  queryOptions?: EventDateSearch;
  select?: (data: EventDatesResponse<EventDate[]>) => TData;
}) => {
  const StoreApi = useStoreApi();
  const params = new URLSearchParams();

  if (causeId) {
    params.append('cause_id', causeId);
  }

  if (include) {
    params.append('include', include);
  }

  const { queryString, queryObject, queryPagination } = createQueryOptions<EventDateSearch>({
    search: queryOptions,
    pagination,
    existingSearchString: params.toString(),
  });

  const getDates = async () => {
    const { data } = await StoreApi.get<EventDatesResponse<EventDate[]>>(
      `/store_items/${eventId}/event_dates?${queryString}`
    );
    return data;
  };

  const key =
    !!causeId && !!eventId ? storeKeys.id(causeId).items.events.id(eventId).dates.all : [];

  return useQuery({
    queryKey: [...key, queryObject, queryPagination, include],
    queryFn: getDates,
    enabled: !!causeId && !!eventId,
    placeholderData: keepPreviousData,
    select,
  });
};
const useInfiniteListEventDates = ({
  eventId,
  causeId,
  queryOptions,
  _enabled = true,
}: {
  causeId?: string;
  eventId?: string;
  queryOptions?: EventDateSearch;
  _enabled?: boolean;
}) => {
  const StoreApi = useStoreApi();

  const getInfiniteEventDates = useCallback(
    async ({ pageParam }: { pageParam: { limit: number; offset: number } }) => {
      const params = new URLSearchParams();

      if (causeId) {
        params.append('cause_id', causeId);
      }

      const { queryString } = createQueryOptions<EventDateSearch>({
        search: queryOptions,
        pagination: {
          limit: pageParam.limit,
          offset: pageParam.offset,
        },
        existingSearchString: params.toString(),
      });

      const { data } = await StoreApi.get<EventDatesResponse<EventDate[]>>(
        `/store_items/${eventId}/event_dates?${queryString}`
      );

      return data;
    },
    [causeId, eventId, queryOptions, StoreApi]
  );

  const queryKey =
    causeId && eventId ? storeKeys.id(causeId).items.events.id(eventId).dates.all : [];

  return useInfiniteQuery({
    queryKey: [...queryKey, queryOptions],
    queryFn: getInfiniteEventDates,
    enabled: !!causeId && !!eventId && _enabled,
    initialPageParam: { limit: 50, offset: 0 },
    getNextPageParam: (lastPage) => {
      if (!lastPage) return undefined;
      const { meta } = lastPage;
      if (!meta) return undefined;
      const { has_more, limit, offset } = meta;
      if (!has_more) {
        return undefined;
      }
      return { limit, offset: offset + limit };
    },
  });
};

const useShowEventDate = <TData = ShowEventResponse>({
  eventId,
  causeId,
  dateId,
  select,
  accessCode,
  include = 'event_date_tickets,cart_item_count',
  enabled = true,
}: {
  eventId?: string;
  causeId?: string;
  dateId?: string;
  select?: (data: ShowEventResponse) => TData;
  accessCode?: string;
  include?: EventDateIncludeOptions;
  enabled?: boolean;
}) => {
  const StoreApi = useStoreApi();

  const queryParams = new URLSearchParams({});

  if (causeId) {
    queryParams.append('cause_id', causeId);
  }

  if (accessCode) {
    queryParams.append('access_code', accessCode);
  }
  if (include) {
    queryParams.append('include', include);
  }

  const getDate = async () => {
    const { data } = await StoreApi.get<ShowEventResponse>(
      `/store_items/${eventId}/event_dates/${dateId}?${queryParams.toString()}`
    );
    return data;
  };

  const key =
    causeId && eventId && dateId
      ? storeKeys.id(causeId).items.events.id(eventId).dates.id(dateId).detail
      : [];

  return useQuery({
    queryKey: [...key, queryParams.toString()],
    queryFn: getDate,
    enabled: !!causeId && !!eventId && !!dateId && enabled,
    select,
  });
};

const useListEventDateTicketInventories = ({
  eventId,
  causeId,
  dateId,
  enabled = true,
}: {
  eventId?: string;
  causeId?: string;
  dateId?: string;
  enabled?: boolean;
}) => {
  const StoreApi = useStoreApi();

  const getEventDateTicketInventories = async () => {
    const params = new URLSearchParams({
      include: 'event_date_tickets,inventory',
    });

    if (causeId) {
      params.append('cause_id', causeId);
    }

    const { data } = await StoreApi.get<{ event_date: EventDate }>(
      `/store_items/${eventId}/event_dates/${dateId}?${params.toString()}`
    );

    return data.event_date;
  };

  const key =
    causeId && eventId && dateId
      ? storeKeys.id(causeId).items.events.id(eventId).dates.id(dateId).inventories
      : [];

  return useQuery({
    queryKey: key,
    queryFn: getEventDateTicketInventories,
    enabled: !!causeId && !!eventId && !!dateId && enabled,
  });
};

const useShowEventDateInventorySummary = <TData = ShowEventInventorySummaryResponse>({
  eventId,
  causeId,
  dateId,
  select,
}: {
  eventId?: string;
  causeId?: string;
  dateId?: string;
  select?: (data: ShowEventInventorySummaryResponse) => TData;
}) => {
  const StoreApi = useStoreApi();
  const getDate = async () => {
    const { data } = await StoreApi.get<ShowEventInventorySummaryResponse>(
      `/store_items/${eventId}/event_dates/${dateId}/inventory_summary?cause_id=${causeId}`
    );
    return data;
  };

  const key =
    causeId && eventId && dateId
      ? storeKeys.id(causeId).items.events.id(eventId).dates.id(dateId).inventorySummary
      : [];

  return useQuery({
    queryKey: [...key],
    queryFn: getDate,
    enabled: !!causeId && !!eventId && !!dateId,
    select,
  });
};

type GetEventDateInventoriesMeta = {
  causeId?: string;
  eventId?: string;
  queryString?: string;
};

const useGetEventDateInventories = () => {
  const StoreApi = useStoreApi();
  const getEventDateInventories = async (context: { meta?: GetEventDateInventoriesMeta }) => {
    const { eventId, queryString } = context.meta || {};
    const { data } = await StoreApi.get<EventDateTicketInventoriesResponse>(
      `/store_items/${eventId}/event_dates/inventory?${queryString}`
    );

    return data;
  };

  return getEventDateInventories;
};

const useListAllEventDateInventories = ({
  eventId,
  causeId,
  queryOptions,
  pagination,
}: {
  eventId?: string;
  causeId?: string;
  queryOptions: EventDateInventoriesSearch;
  pagination?: QueryPagination;
}) => {
  const getEventDateInventories = useGetEventDateInventories();

  const searchParams = new URLSearchParams();

  if (causeId) {
    searchParams.append('cause_id', causeId);
  }

  const { queryString, queryObject, queryPagination } =
    createQueryOptions<EventDateInventoriesSearch>({
      search: queryOptions,
      pagination,
      existingSearchString: searchParams.toString(),
    });

  const key =
    causeId && eventId ? storeKeys.id(causeId).items.events.id(eventId).dates.allInventories : [];

  return useQuery({
    queryKey: [...key, queryObject, queryPagination],
    queryFn: getEventDateInventories,
    meta: { queryString, eventId, causeId },
    enabled: !!causeId && !!eventId,
  });
};

export {
  useGetEventDateInventories,
  useInfiniteListEventDates,
  useListAllEventDateInventories,
  useListEventDateTicketInventories,
  useListEventDates,
  useShowEventDate,
  useShowEventDateInventorySummary,
};
